import { joiResolver } from '@hookform/resolvers/joi';
import { Box, Grid, Link, Paper, Typography } from '@mui/material';
import Joi from 'joi';
import { observer } from 'mobx-react-lite';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import settings from '../../../global/config/settings';
import { API } from '../../../global/config/api';
import { XsButton, XsTextField } from '../../../global/ui';
import { useServerCall } from '../../../global/helpers/hooks';
import { isEmailValid, isPasswordValid } from '../../../global/helpers/validators';
import { persistLogin } from '../../../global/helpers/actions';
import GlobalStore from '../../../global/stores/GlobalStore';
import UIStore from '../../../global/stores/UIStore';
import LoginStore from './LoginStore';

interface ILoginForm {
  log_in: string;
  password: string;
}

function Login() {
  const { t } = useTranslation();
  const history = useHistory();

  const schema = Joi.object<ILoginForm>({
    log_in: Joi.string()
      .custom((v, h) => (isEmailValid(v) ? v : h.error('any.invalid')))
      .required(),
    password: Joi.string()
      .custom((v, h) => (isPasswordValid(v) ? v : h.error('any.invalid')))
      .required()
  });

  // POZN.: `XsTextField` číta chyby cez `useError` → `useFormContext()`, preto MUSÍ byť
  // formulár obalený `<FormProvider {...form}>`. Tento vzor platí pre VŠETKY formuláre (Phase 5/6).
  const form = useForm<ILoginForm>({ resolver: joiResolver(schema) });
  const { control, handleSubmit } = form;

  const loginCall = useServerCall<{ login: string; password: string }, any>({
    key: 'login',
    mode: 'control',
    request: { method: 'POST' },
    queryOptions: { refetchOnWindowFocus: false },
    onSuccess: (res) => {
      persistLogin(res.data); // ulož token + user, nastav axios Authorization
      GlobalStore.loading(false);
      history.push('/requests');
    },
    onError: () => {
      GlobalStore.loading(false);
      UIStore.setNotificationMessage(t('login.bad_credentials'), 'error');
    }
  });

  const onSubmit = handleSubmit(async (data) => {
    GlobalStore.loading(true, t('login.signing_in'));
    const recaptcha = await LoginStore.getRecaptchaToken('security_login');
    loginCall.run({ url: API.login(recaptcha), data: { login: data.log_in, password: data.password } });
  });

  return (
    <Grid container direction='column' spacing={3}>
      <Grid item className='textCenter'>
        <Typography variant='h3'>{t('login.title')}</Typography>
      </Grid>
      <Grid item>
        <Paper variant='elevation' style={{ backgroundColor: 'transparent' }}>
          <Box p={2}>
            <form onSubmit={onSubmit}>
              <FormProvider {...form}>
                <Grid container direction='column' spacing={4}>
                  <Grid item>
                    <Typography variant='body2'>{t('login.intro')}</Typography>
                  </Grid>
                  <Grid item>
                    <Controller
                      name='log_in'
                      control={control}
                      defaultValue=''
                      render={({ field }) => <XsTextField {...field} autoFocus label={t('login.login_label')} />}
                    />
                  </Grid>
                  <Grid item>
                    <Controller
                      name='password'
                      control={control}
                      defaultValue=''
                      render={({ field }) => (
                        <XsTextField {...field} type='password' label={t('login.password_label')} />
                      )}
                    />
                  </Grid>
                </Grid>
                <Grid container direction='column' spacing={2} className='mt-2'>
                  <Grid item>
                    <XsButton type='submit' variant='contained' label='login.submit' />
                  </Grid>
                  {GlobalStore.enabledRecaptchaV2 && (
                    <Grid item>
                      <ReCAPTCHA
                        sitekey={settings.SITE_KEY_V2 ?? ''}
                        onChange={(tok) => (GlobalStore.tokenRecaptchaV2 = tok ?? undefined)}
                      />
                    </Grid>
                  )}
                  <Grid item>
                    <Link variant='body2' className='pointer' onClick={() => LoginStore.setScreen('forgot')}>
                      {t('login.forgot_link')}
                    </Link>
                  </Grid>
                </Grid>
              </FormProvider>
            </form>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
}

export default observer(Login);
